import { LanderCookie } from "../commons/Constants";
import { getCookie } from "../commons/HelperFunctions";
import Logger from "../commons/Logger";

const ROLLER_ADS_CONFIG = {
  zeroClick: {
    endpoint: "https://309.eu.roller.ad/get-direct-click/godaddy/any",
    pid: "309",
    pk: "afa69c866-75ac4fb7f8ecd",
  },
  keywordClick: {
    endpoint: "https://343.eu.roller.ad/get-direct-click/godaddy1click/any",
    pid: "343",
    pk: "beeeb4834-ef89857d340e8",
  },
  backfill: {
    endpoint: "https://309.eu.roller.ad/get-direct-click/godaddy/any",
    pid: "309",
    pk: "afa69c866-75ac4fb7f8ecd",
  },
};

export interface RollerAd {
  url: string;
  bid: number;
  campaign_id?: number;
}

export interface RollerAdsResponse {
  ads: RollerAd[];
}

export interface RollerAdsFetchResult {
  status: number;
  ads: RollerAd[];
  error?: string;
}

interface RollerAdsFetchParams {
  rootDomain: string;
  customerId?: string;
  keywords?: string[];
  /** Use the keyword click endpoint (different RollerAds campaign with its own path/pid/pk). Defaults to 0-click auto-redirect endpoint. */
  keywordClick?: boolean;
  /** Use the backfill endpoint for Yahoo no-ads fallback. Takes priority over keywordClick. */
  backfill?: boolean;
}

function getConfig(params: RollerAdsFetchParams) {
  if (params.backfill) return ROLLER_ADS_CONFIG.backfill;
  if (params.keywordClick) return ROLLER_ADS_CONFIG.keywordClick;
  return ROLLER_ADS_CONFIG.zeroClick;
}

export async function fetchRollerAds(
  params: RollerAdsFetchParams
): Promise<RollerAdsFetchResult> {
  // caf_ipaddr_override: set via ModHeader to test with a specific IP (e.g. 38.175.157.213 returns ads)
  const ip =
    getCookie("caf_ipaddr_override") || getCookie(LanderCookie.CLIENT_IP);
  if (!ip) {
    return { status: 0, ads: [], error: "No client IP available" };
  }

  const config = getConfig(params);

  const searchParams = new URLSearchParams({
    pid: config.pid,
    pk: config.pk,
    ua: navigator.userAgent,
    ip,
    lang: navigator.language || "en",
    subid1: params.rootDomain,
    ...(params.customerId && { subid2: params.customerId }),
  });

  if (params.keywords?.length) {
    searchParams.set("keywords", params.keywords.join(","));
  }

  if (document.referrer) {
    searchParams.set("ref", document.referrer);
  }

  const url = `${config.endpoint}?${searchParams.toString()}`;
  Logger.debug("RollerAds fetch", {
    endpoint: config.endpoint,
    rootDomain: params.rootDomain,
    hasKeywords: Boolean(params.keywords?.length),
    keywordClick: Boolean(params.keywordClick),
  });

  try {
    const response = await fetch(url, {
      credentials: "omit",
    });

    if (response.status === 204) {
      return { status: 204, ads: [] };
    }

    if (!response.ok) {
      return {
        status: response.status,
        ads: [],
        error: `HTTP ${response.status}`,
      };
    }

    const data: RollerAdsResponse = await response.json();
    return { status: 200, ads: data.ads || [] };
  } catch (error) {
    Logger.error("RollerAds fetch error:", error);
    return { status: 0, ads: [], error: String(error) };
  }
}
